export type DataTableSortOrder = 'asc' | 'desc';

/** Column filter UI / match mode for `app-data-table`. */
export type DataTableColumnFilterMode = 'text' | 'select' | 'number' | 'date' | 'auto';

export interface DataTableSortChange {
  sortBy: string;
  sortOrder: DataTableSortOrder;
}

/** Active per-column filter values (`columnId` → query string). */
export type DataTableColumnFilters = Record<string, string>;

/** Right-click on a data-table body row (`rowContextMenu` output). */
export interface DataTableRowContextMenuEvent<T = unknown> {
  row: T;
  event: MouseEvent;
}

/** Left-click / keyboard activate / double-click on a clickable body row. */
export interface DataTableRowInteractionEvent<T = unknown> {
  row: T;
  event: MouseEvent | KeyboardEvent;
}

export interface DataTableColumn<T = unknown> {
  id: string;
  label: string;
  /**
   * When omitted, the column is sortable (`true`).
   * Set `false` for actions, checkboxes, expand toggles, and other non-comparable UI columns.
   */
  sortable?: boolean;
  /**
   * When omitted, the column is filterable (`true`) with the same reserved-id opt-out as sort
   * (`actions` / `select` / `expand`). Set `false` for UI-only columns.
   */
  filterable?: boolean;
  /**
   * When `false`, the column stays on screen but is omitted from the toolbar Excel export.
   * Action, selection, and expand columns are omitted even when this is unset.
   */
  exportable?: boolean;
  /**
   * When omitted, the column can be manually resized by dragging its header edge (`true`),
   * except for narrow action/selection/expand columns. Set `false` to opt out.
   */
  resizable?: boolean;
  /**
   * Filter UI/mode. Default `auto`: dropdown of distinct values when cardinality is small,
   * otherwise text contains. Use `number` for range/`>`/`<`/`=` parsing; `date` for ISO/locale
   * day equality or `from..to` ranges.
   */
  filter?: DataTableColumnFilterMode;
  /** Max distinct values before `auto` falls back to text (default 12). */
  filterSelectMax?: number;
  align?: 'left' | 'right' | 'center';
  nowrap?: boolean;
  /** Výchozí textová buňka, nebo `template` + `appDataTableCell`. */
  cell?: 'text' | 'template';
  /** Šablona záhlaví sloupce (`appDataTableHeaderCell`). */
  headerCell?: 'template';
  /** Fuller name for the header tooltip when `label` is abbreviated. Also kept on `<th>`. */
  headerTitle?: string;
  headerClass?: string;
  cellClass?: string;
  format?: (row: T) => string | number | null | undefined;
  /**
   * Optional sort key when the displayed cell is a template (or `format` is display-only).
   * Used by built-in client sort / `sortDataTableRows`.
   */
  sortValue?: (row: T) => string | number | boolean | Date | null | undefined;
  /**
   * Optional filter key when the displayed cell is a template (or `format` is display-only).
   * Falls back to `sortValue` → `format` → `row[column.id]`.
   */
  filterValue?: (row: T) => string | number | boolean | Date | null | undefined;
  title?: (row: T) => string | undefined;
  emptyText?: string;
  /** U šablonové buňky zastavit propagaci kliknutí na řádek. */
  stopRowClick?: boolean;
}

export function formatDataTableCell(value: unknown, emptyText = '—'): string {
  if (value === null || value === undefined || value === '') {
    return emptyText;
  }
  return String(value);
}
